import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { UserContextService } from 'src/services/user-context.service';
import { AuthenticationHttpService } from 'src/providers/authentication-http.service';
import { RoutePermission } from 'src/app/core/models/route-permission-key';

/**
 * Redirect Handler Service
 *
 * Handles complex redirect logic that was previously managed by UI-Router's rules engine.
 * This service provides programmatic redirects for scenarios that require runtime decision-making.
 *
 * Use Cases:
 * - Permission-based redirects (redirect to first accessible route)
 * - User role-based redirects (different default pages for different user types)
 * - Feature flag-based redirects (redirect based on enabled features)
 * - Authentication state-based redirects (different flows for authenticated vs unauthenticated)
 *
 * Migration Notes:
 * - Replaces complex UI-Router rule functions with Angular Router guards and services
 * - Maintains the same redirect logic while providing better testability and maintainability
 */
@Injectable({
  providedIn: 'root'
})
export class RedirectHandlerService {

  constructor(
    private router: Router,
    private userContextService: UserContextService,
    private authenticationService: AuthenticationHttpService
  ) {}

  /**
   * Handle dashboard redirect based on user permissions
   * Equivalent to UI-Router rule that checks permissions before redirecting
   */
  handleDashboardRedirect(): void {
    // Default dashboard redirect logic
    if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardActivity)) {
      this.router.navigate(['/dashboard/activity']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardLicenses)) {
      this.router.navigate(['/dashboard/licenses']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardCompliance)) {
      this.router.navigate(['/dashboard/compliance']);
    } else {
      // Fallback if no dashboard permissions
      this.router.navigate(['/profile/overview']);
    }
  }

  /**
   * Handle network redirect with permission fallbacks
   * Migrates the onNetworkTransitionSuccess logic from UI-Router
   */
  handleNetworkRedirect(): void {
    if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkEntities)) {
      this.router.navigate(['/network/entities']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkAgencyList)) {
      this.router.navigate(['/network/agencyList']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkCarrierList)) {
      this.router.navigate(['/network/carrierList']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkHierarchyList)) {
      this.router.navigate(['/network/hierarchyList']);
    } else {
      // No network permissions - redirect to error
      this.router.navigate(['/error']);
    }
  }

  /**
   * Handle profile redirect based on user type and permissions
   */
  handleProfileRedirect(): void {
    // Check user type and redirect accordingly
    const userContext = this.userContextService.userContext;

    if (userContext?.account?.accountType === 'individual') {
      this.router.navigate(['/profile/overview']);
    } else if (userContext?.account?.accountType === 'agency') {
      this.router.navigate(['/profile/licenses']);
    } else {
      // Default fallback
      this.router.navigate(['/profile/overview']);
    }
  }

  /**
   * Handle authentication-based redirect
   * Determines where to send users after login based on their context
   */
  handlePostLoginRedirect(returnUrl?: string): void {
    if (returnUrl) {
      // User had a specific destination in mind
      this.router.navigate([returnUrl]);
      return;
    }

    // Determine best default landing page based on user permissions
    if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardActivity)) {
      this.router.navigate(['/dashboard/activity']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkEntities)) {
      this.router.navigate(['/network/entities']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.profileOverview)) {
      this.router.navigate(['/profile/overview']);
    } else {
      // Fallback - should rarely hit this
      this.router.navigate(['/error'], { queryParams: { type: 'no_permissions' } });
    }
  }

  /**
   * Handle legacy route redirects with parameter preservation
   * For routes that need to redirect but preserve query parameters and route data
   */
  handleLegacyRedirect(route: ActivatedRouteSnapshot, newPath: string): void {
    const queryParams = route.queryParams;
    const fragment = route.fragment;

    this.router.navigate([newPath], {
      queryParams,
      fragment,
      queryParamsHandling: 'preserve'
    });
  }

  /**
   * Handle conditional redirects based on feature flags
   * Useful for A/B testing or gradual feature rollouts
   */
  handleFeatureBasedRedirect(featureName: string, enabledPath: string, disabledPath: string): void {
    // Check if feature is enabled (you'd integrate with your feature flag service)
    const isFeatureEnabled = this.userContextService.isFeatureToggleEnabled(featureName);

    if (isFeatureEnabled) {
      this.router.navigate([enabledPath]);
    } else {
      this.router.navigate([disabledPath]);
    }
  }

  /**
   * Handle mobile vs desktop redirects
   * Redirect to different routes based on device type
   */
  handleDeviceBasedRedirect(mobileRoute: string, desktopRoute: string): void {
    const isMobile = window.innerWidth < 768; // Simple mobile detection

    if (isMobile) {
      this.router.navigate([mobileRoute]);
    } else {
      this.router.navigate([desktopRoute]);
    }
  }

  /**
   * Utility method to check if a redirect is needed based on current route
   */
  shouldRedirect(currentRoute: string, targetRoute: string): boolean {
    return currentRoute === targetRoute;
  }

  /**
   * Get the appropriate redirect URL based on user context
   * Used by guards that need to determine redirect destinations
   */
  getRedirectUrl(fallbackUrl: string = '/dashboard/activity'): string {
    // Logic to determine the best redirect URL for the current user
    if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardActivity)) {
      return '/dashboard/activity';
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkEntities)) {
      return '/network/entities';
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.profileOverview)) {
      return '/profile/overview';
    } else {
      return fallbackUrl;
    }
  }
}
